import type {
  ArchetypeWithSubarchetypes,
  Deck,
  Event,
  EventResult,
  EventResultDecklist,
  League,
  LeagueStandings,
  MatchesQuery,
  MatchesResponse,
  MetagameQuery,
  MetagameResponse,
  OpensResponse,
  Player,
  Subarchetype,
  SubarchetypeResultsResponse,
  UpcomingMeleeEventsResponse,
} from "@liga/shared";

const BASE = "/api";

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`POST ${path} failed: ${res.status} ${text}`);
  }
  return (await res.json()) as T;
}

async function patch<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PATCH ${path} failed: ${res.status} ${text}`);
  }
  return (await res.json()) as T;
}

function qs(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s.length === 0 ? "" : `?${s}`;
}

export interface AdminUser {
  email: string;
  name: string;
  picture: string | null;
}

export interface AuthMeResponse {
  configured: boolean;
  user: AdminUser | null;
}

// ---- Melee import (admin) ---------------------------------------------------

export interface MeleeReviewStanding {
  rank: number;
  meleeName: string;
  resolvedPlayerName: string | null;
  points: number;
  deckName: string | null;
  /** Pre-filled when the deck name already exists in the local catalogue. */
  suggestedSubarchetypeId: number | null;
  /** melee.gg decklist id for this standing — opaque, forwarded as-is to publish. */
  meleeDecklistId: string | null;
}

export interface MeleeReviewMatch {
  round: number;
  meleeResultString: string;
  player1MeleeName: string;
  player2MeleeName: string | null;
  player1ResolvedName: string | null;
  player2ResolvedName: string | null;
  player1Wins: number;
  player2Wins: number;
  draws: number;
  isIntentionalDraw: boolean;
  isBye: boolean;
  winnerMeleeName: string | null;
  needsReview: boolean;
  reviewReason?: string;
}

export interface MeleeReviewPayload {
  meleeEventId: string;
  numberOfPlayers: number;
  standings: MeleeReviewStanding[];
  matches: MeleeReviewMatch[];
}

export interface MeleeLoadRequest {
  meleeEventId: string;
  eventDate: string;
  eventName: string;
  leagueName?: string | null;
}

export interface MeleeLoadResponse {
  meleeEventId: string;
  eventDate: string;
  eventName: string;
  leagueName: string | null;
  payload: MeleeReviewPayload;
}

export interface MeleeAliasesResponse {
  aliases: Array<{ meleeName: string; playerName: string }>;
  players: string[];
}

export interface MeleePublishStanding {
  rank: number;
  meleeName: string;
  playerName: string;
  points: number;
  deckName: string | null;
  /**
   * Subarchetype for the resulting `event_result` row.
   * - `number` sets the classification.
   * - `null` clears it (leaving the row "Sem classificação").
   * - Omit (`undefined`) to leave whatever was there before untouched.
   */
  subarchetypeId?: number | null;
  /**
   * melee.gg decklist id; when set, the back-end fetches the card list from
   * `/Decklist/View/<id>` and snapshots it under this standing's
   * `event_result` row. Omit (undefined) when there's no decklist.
   */
  meleeDecklistId?: string | null;
}

export interface MeleePublishMatch {
  round: number;
  player1MeleeName: string;
  player2MeleeName: string | null;
  player1Name: string;
  player2Name: string | null;
  player1Wins: number;
  player2Wins: number;
  draws: number;
  isIntentionalDraw: boolean;
  winnerName: string | null;
}

export interface MeleePublishRequest {
  meleeEventId: string;
  eventDate: string;
  eventName: string;
  leagueName?: string | null;
  /**
   * Editorial tag applied by the admin at import time. Not sourced from
   * Melee — this is just the "Marcar como Open" checkbox in the import
   * form. Setting it to `true` tags the resulting event with
   * `events.is_open = 1`; omitting it (or sending `false`) leaves a
   * previously-set flag untouched on re-publish.
   */
  isOpen?: boolean;
  standings: MeleePublishStanding[];
  matches: MeleePublishMatch[];
  aliases?: Array<{ meleeName: string; playerName: string }>;
}

export interface MeleePublishResponse {
  eventId: number;
  leagueId: number | null;
}

// ---- Admin league CRUD ------------------------------------------------------

export interface AdminLeague {
  id: number;
  name: string;
  year: number;
  startDate: string;
  endDate: string;
  durationMonths: number;
  etapasPerMonth: number[] | null;
  /** Number of events currently linked to the league. */
  eventCount: number;
}

export interface AdminLeagueWriteBody {
  name: string;
  year: number;
  startDate: string;
  endDate: string;
  durationMonths: number;
  etapasPerMonth: number[] | null;
}

export interface AdminEventPatchBody {
  leagueId?: number | null;
  name?: string;
  date?: string;
  format?: string | null;
  meleeEventId?: string | null;
  /** Toggle the "Open" tag; see `Event.isOpen` in `@liga/shared`. */
  isOpen?: boolean;
}

export interface AdminDeck {
  id: number;
  name: string;
  /** Legacy free-text archetype label from old spreadsheet imports. */
  archetype: string | null;
  useCount: number;
}

export interface AdminDeckDecklistRow {
  eventResultId: number;
  eventId: number;
  eventName: string;
  eventDate: string;
  playerId: number;
  playerName: string;
  rank: number;
  points: number;
  /** `true` when the snapshot in `event_result_decklist_cards` has at least one row. */
  hasDecklist: boolean;
  meleeDecklistId: string | null;
  /**
   * Per-event_result subarchetype. This is the *only* place classification
   * lives — `null` means "Sem classificação" (no inherited deck-level
   * default anymore).
   */
  subarchetypeId: number | null;
  subarchetypeName: string | null;
}

export interface AdminDeckDecklistsResponse {
  deck: AdminDeck;
  decklists: AdminDeckDecklistRow[];
}

export interface AdminUnclassifiedRow {
  eventResultId: number;
  eventId: number;
  eventName: string;
  eventDate: string;
  playerId: number;
  playerName: string;
  /** `null` when the event_result has no deck attached at all. */
  deckId: number | null;
  deckName: string | null;
  rank: number;
  points: number;
  hasDecklist: boolean;
  meleeDecklistId: string | null;
  /**
   * "Most-common subarchetype across history for this deck name", same
   * heuristic the Melee import uses. `null` when there's no historical
   * signal (a brand-new deck name, or `deckName === null`).
   */
  suggestedSubarchetypeId: number | null;
  suggestedSubarchetypeName: string | null;
}

export const api = {
  players: () => get<Player[]>("/players"),
  decks: () => get<Deck[]>("/decks"),
  leagueYears: () => get<number[]>("/leagues/years"),
  leagues: (year?: number) => get<League[]>(`/leagues${qs({ year })}`),
  leagueStandings: (id: number) => get<LeagueStandings>(`/leagues/${id}/standings`),
  events: (leagueId?: number) => get<Event[]>(`/events${qs({ leagueId })}`),
  event: (id: number) => get<Event>(`/events/${id}`),
  eventResults: (id: number) => get<EventResult[]>(`/events/${id}/results`),
  eventResultDecklist: (eventId: number, resultId: number) =>
    get<EventResultDecklist>(`/events/${eventId}/results/${resultId}/decklist`),
  matches: (q: MatchesQuery) => get<MatchesResponse>(`/matches${qs({ ...q })}`),
  metagame: (q: MetagameQuery = {}) =>
    get<MetagameResponse>(`/metagame${qs({ ...q })}`),
  opens: (q: { year?: number } = {}) =>
    get<OpensResponse>(`/opens${qs({ ...q })}`),
  opensYears: () => get<number[]>("/opens/years"),
  meleeUpcomingEvents: (q: { organizationId?: string; format?: string } = {}) =>
    get<UpcomingMeleeEventsResponse>(`/melee/upcoming-events${qs({ ...q })}`),
  authMe: () => get<AuthMeResponse>("/auth/me"),
  authLogout: () => post<{ ok: boolean }>("/auth/logout"),
  meleeAliases: () => get<MeleeAliasesResponse>("/admin/melee/aliases"),
  meleeUpsertAlias: (body: { meleeName: string; playerName: string }) =>
    post<{ meleeName: string; playerName: string }>("/admin/melee/aliases", body),
  meleeLoad: (body: MeleeLoadRequest) =>
    post<MeleeLoadResponse>("/admin/melee/load", body),
  meleePublish: (body: MeleePublishRequest) =>
    post<MeleePublishResponse>("/admin/melee/publish", body),
  adminLeagues: () => get<AdminLeague[]>("/admin/leagues"),
  adminCreateLeague: (body: AdminLeagueWriteBody) =>
    post<AdminLeague>("/admin/leagues", body),
  adminUpdateLeague: (id: number, body: Partial<AdminLeagueWriteBody>) =>
    patch<AdminLeague>(`/admin/leagues/${id}`, body),
  adminUpdateEvent: (id: number, body: AdminEventPatchBody) =>
    patch<Event>(`/admin/events/${id}`, body),
  archetypes: () => get<ArchetypeWithSubarchetypes[]>("/archetypes"),
  subarchetypeResults: (id: number) =>
    get<SubarchetypeResultsResponse>(`/subarchetypes/${id}/results`),
  adminArchetypes: () =>
    get<ArchetypeWithSubarchetypes[]>("/admin/archetypes"),
  adminCreateArchetype: (body: { name: string }) =>
    post<{ id: number; name: string }>("/admin/archetypes", body),
  adminRenameArchetype: (id: number, body: { name: string }) =>
    patch<{ id: number; name: string }>(`/admin/archetypes/${id}`, body),
  adminSubarchetypes: () => get<Subarchetype[]>("/admin/subarchetypes"),
  adminCreateSubarchetype: (body: { name: string; archetypeId: number }) =>
    post<{ id: number; name: string; archetypeId: number }>(
      "/admin/subarchetypes",
      body,
    ),
  adminUpdateSubarchetype: (
    id: number,
    body: { name?: string; archetypeId?: number },
  ) => patch<{ id: number }>(`/admin/subarchetypes/${id}`, body),
  adminDecks: () => get<AdminDeck[]>("/admin/decks"),
  adminDeckDecklists: (id: number) =>
    get<AdminDeckDecklistsResponse>(`/admin/decks/${id}/decklists`),
  adminUnclassifiedEventResults: () =>
    get<AdminUnclassifiedRow[]>("/admin/event-results/unclassified"),
  adminSetEventResultSubarchetype: (
    id: number,
    body: { subarchetypeId: number | null },
  ) =>
    patch<{ id: number; subarchetypeId: number | null }>(
      `/admin/event-results/${id}`,
      body,
    ),
  adminSetSubarchetypeRepresentativeCard: (
    id: number,
    body: { cardName: string | null },
  ) =>
    patch<{ subarchetype: Subarchetype | null }>(
      `/admin/subarchetypes/${id}/representative-card`,
      body,
    ),
};
